/**
 * Advanced API Manager - Global HTTP Client
 * 
 * A comprehensive, reusable API client with:
 * - Automatic base URL configuration
 * - Global headers management
 * - Request/response interceptors
 * - Error handling and retry logic
 * - Request cancellation
 * - Timeout management
 * - Type safety
 * - Request/response logging
 */

import { config } from '../utils/config';

// ==================== Types ====================

export interface ApiRequestConfig {
    headers?: Record<string, string>;
    params?: Record<string, string | number | boolean>;
    timeout?: number;
    withAuth?: boolean;
    retry?: number;
    retryDelay?: number;
    signal?: AbortSignal;
}

export interface ApiResponse<T = unknown> {
    success: boolean;
    data?: T;
    message?: string;
    error?: string;
    statusCode?: number;
    timestamp?: string;
}

export interface ApiError {
    message: string;
    statusCode?: number;
    code?: string;
    details?: unknown;
}

// ==================== API Manager Class ====================

class ApiManager {
    private baseURL: string;
    private defaultHeaders: Record<string, string>;
    private timeout: number;
    private retryAttempts: number;
    private retryDelay: number;

    // Request/Response Interceptors
    private requestInterceptors: Array<(config: RequestInit) => RequestInit> = [];
    private responseInterceptors: Array<(response: Response) => Response | Promise<Response>> = [];

    constructor() {
        this.baseURL = config.API_BASE_URL;
        this.timeout = 30000; // 30 seconds default
        this.retryAttempts = 3;
        this.retryDelay = 1000; // 1 second

        this.defaultHeaders = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Client-Version': '1.0.0',
            'X-Platform': 'web',
        };

        // Add API key if available
        if (config.API_KEY) {
            this.defaultHeaders['key'] = config.API_KEY;
        }
    }

    // ==================== Configuration Methods ====================

    /**
     * Set or update base URL
     */
    setBaseURL(url: string): void {
        this.baseURL = url;
        console.log('📡 API Base URL updated: - apiManager.ts:84', url);
    }

    /**
     * Set default timeout for all requests
     */
    setTimeout(ms: number): void {
        this.timeout = ms;
    }

    /**
     * Set default retry configuration
     */
    setRetryConfig(attempts: number, delay: number): void {
        this.retryAttempts = attempts;
        this.retryDelay = delay;
    }

    /**
     * Add or update a default header
     */
    setHeader(key: string, value: string): void {
        this.defaultHeaders[key] = value;
    }

    /**
     * Remove a default header
     */
    removeHeader(key: string): void {
        delete this.defaultHeaders[key];
    }

    /**
     * Get all default headers
     */
    getHeaders(): Record<string, string> {
        return { ...this.defaultHeaders };
    }

    // ==================== Interceptor Methods ====================

    /**
     * Add request interceptor
     */
    addRequestInterceptor(interceptor: (config: RequestInit) => RequestInit): void {
        this.requestInterceptors.push(interceptor);
    }

    /**
     * Add response interceptor
     */
    addResponseInterceptor(interceptor: (response: Response) => Response | Promise<Response>): void {
        this.responseInterceptors.push(interceptor);
    }

    // ==================== Helper Methods ====================

    /**
     * Build full URL with query parameters
     */
    private buildURL(endpoint: string, params?: Record<string, string | number | boolean>): string {
        // Remove leading slash from endpoint if present
        const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;

        // Ensure baseURL ends with /api if not already there
        let fullBaseURL = this.baseURL;
        if (!fullBaseURL.endsWith('/api') && !fullBaseURL.endsWith('/api/')) {
            fullBaseURL = `${fullBaseURL}/api`;
        }

        const url = new URL(cleanEndpoint, fullBaseURL);

        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                url.searchParams.append(key, String(value));
            });
        }

        return url.toString();
    }

    /**
     * Merge headers with defaults
     */
    private mergeHeaders(customHeaders?: Record<string, string>): Record<string, string> {
        return {
            ...this.defaultHeaders,
            ...customHeaders,
        };
    }

    /**
     * Create timeout promise
     */
    private createTimeout(ms: number): Promise<never> {
        return new Promise((_, reject) => {
            setTimeout(() => {
                reject(new Error(`Request timeout after ${ms}ms`));
            }, ms);
        });
    }

    /**
     * Delay helper for retry
     */
    private delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Apply request interceptors
     */
    private applyRequestInterceptors(config: RequestInit): RequestInit {
        return this.requestInterceptors.reduce(
            (acc, interceptor) => interceptor(acc),
            config
        );
    }

    /**
     * Apply response interceptors
     */
    private async applyResponseInterceptors(response: Response): Promise<Response> {
        let processedResponse = response;
        for (const interceptor of this.responseInterceptors) {
            processedResponse = await interceptor(processedResponse);
        }
        return processedResponse;
    }

    /**
     * Log request (development only)
     */
    private logRequest(method: string, url: string, requestConfig?: RequestInit): void {
        if (config.APP_ENV === 'development') {
            console.group(`🚀 API Request: ${method} ${url}`);
            console.log('Headers: - apiManager.ts:220', requestConfig?.headers);
            if (requestConfig?.body) {
                console.log('Body: - apiManager.ts:222', requestConfig.body);
            }
            console.groupEnd();
        }
    }

    /**
     * Log response (development only)
     */
    private logResponse(url: string, response: Response, data?: unknown): void {
        if (config.APP_ENV === 'development') {
            console.group(`✅ API Response: ${response.status} ${url}`);
            console.log('Status: - apiManager.ts:234', response.status, response.statusText);
            console.log('Data: - apiManager.ts:235', data);
            console.groupEnd();
        }
    }

    /**
     * Log error (always)
     */
    private logError(url: string, error: unknown): void {
        console.group(`❌ API Error: ${url}`);
        console.error('Error: - apiManager.ts:245', error);
        console.groupEnd();
    }

    // ==================== Core Request Method ====================

    /**
     * Core request method with retry logic
     */
    private async request<T = unknown>(
        method: string,
        endpoint: string,
        data?: unknown,
        config: ApiRequestConfig = {}
    ): Promise<ApiResponse<T>> {
        const {
            headers: customHeaders,
            params,
            timeout = this.timeout,
            retry = this.retryAttempts,
            retryDelay = this.retryDelay,
            signal,
        } = config;

        const url = this.buildURL(endpoint, params);
        const headers = this.mergeHeaders(customHeaders);

        let requestConfig: RequestInit = {
            method,
            headers,
            signal,
        };

        // Add body for POST, PUT, PATCH
        if (data && ['POST', 'PUT', 'PATCH'].includes(method)) {
            requestConfig.body = JSON.stringify(data);
        }

        // Apply request interceptors
        requestConfig = this.applyRequestInterceptors(requestConfig);

        // Log request
        this.logRequest(method, url, requestConfig);

        let lastError: Error | null = null;

        // Retry logic
        for (let attempt = 0; attempt <= retry; attempt++) {
            try {
                // Race between fetch and timeout
                const responsePromise = fetch(url, requestConfig);
                const timeoutPromise = this.createTimeout(timeout);

                const response = await Promise.race([responsePromise, timeoutPromise]) as Response;

                // Apply response interceptors
                const processedResponse = await this.applyResponseInterceptors(response);

                // Handle HTTP errors
                if (!processedResponse.ok) {
                    const errorData = await processedResponse.json().catch(() => ({}));

                    const apiError: ApiError = {
                        message: errorData.message || `HTTP ${processedResponse.status}: ${processedResponse.statusText}`,
                        statusCode: processedResponse.status,
                        code: errorData.code,
                        details: errorData,
                    };

                    this.logError(url, apiError);

                    return {
                        success: false,
                        error: apiError.message,
                        statusCode: apiError.statusCode,
                        timestamp: new Date().toISOString(),
                    };
                }

                // Parse response
                const responseData = await processedResponse.json();

                // Log success
                this.logResponse(url, processedResponse, responseData);

                return {
                    success: true,
                    data: responseData,
                    message: responseData.message,
                    statusCode: processedResponse.status,
                    timestamp: new Date().toISOString(),
                };

            } catch (error) {
                lastError = error as Error;
                this.logError(url, error);

                // Don't retry if request was cancelled
                if (error instanceof Error && error.name === 'AbortError') {
                    return {
                        success: false,
                        error: 'Request cancelled',
                        timestamp: new Date().toISOString(),
                    };
                }

                // Retry logic
                if (attempt < retry) {
                    console.log(`⏳ Retrying... (${attempt + 1}/${retry}) - apiManager.ts:353`);
                    await this.delay(retryDelay * (attempt + 1)); // Exponential backoff
                    continue;
                }
            }
        }

        // All retries failed
        return {
            success: false,
            error: lastError?.message || 'Request failed after retries',
            timestamp: new Date().toISOString(),
        };
    }

    // ==================== HTTP Methods ====================

    /**
     * GET request
     */
    async get<T = unknown>(endpoint: string, config?: ApiRequestConfig): Promise<ApiResponse<T>> {
        return this.request<T>('GET', endpoint, undefined, config);
    }

    /**
     * POST request
     */
    async post<T = unknown>(endpoint: string, data?: unknown, config?: ApiRequestConfig): Promise<ApiResponse<T>> {
        return this.request<T>('POST', endpoint, data, config);
    }

    /**
     * PUT request
     */
    async put<T = unknown>(endpoint: string, data?: unknown, config?: ApiRequestConfig): Promise<ApiResponse<T>> {
        return this.request<T>('PUT', endpoint, data, config);
    }

    /**
     * PATCH request
     */
    async patch<T = unknown>(endpoint: string, data?: unknown, config?: ApiRequestConfig): Promise<ApiResponse<T>> {
        return this.request<T>('PATCH', endpoint, data, config);
    }

    /**
     * DELETE request
     */
    async delete<T = unknown>(endpoint: string, config?: ApiRequestConfig): Promise<ApiResponse<T>> {
        return this.request<T>('DELETE', endpoint, undefined, config);
    }

    // ==================== Convenience Methods ====================

    /**
     * Upload file (multipart/form-data)
     */
    async upload<T = unknown>(
        endpoint: string,
        file: File,
        additionalData?: Record<string, string>,
        config?: ApiRequestConfig
    ): Promise<ApiResponse<T>> {
        const formData = new FormData();
        formData.append('file', file);

        if (additionalData) {
            Object.entries(additionalData).forEach(([key, value]) => {
                formData.append(key, value);
            });
        }

        const uploadConfig: ApiRequestConfig = {
            ...config,
            headers: {
                ...config?.headers,
                // Don't set Content-Type for FormData, browser will set it with boundary
            },
        };

        // Remove Content-Type to let browser set it
        delete uploadConfig.headers?.['Content-Type'];

        const url = this.buildURL(endpoint, config?.params);
        const response = await fetch(url, {
            method: 'POST',
            headers: this.mergeHeaders(uploadConfig.headers),
            body: formData,
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                error: errorData.message || 'Upload failed',
                statusCode: response.status,
            };
        }

        const data = await response.json();
        return {
            success: true,
            data,
            statusCode: response.status,
        };
    }

    /**
     * Download file (blob)
     */
    async download(
        endpoint: string,
        filename: string,
        config?: ApiRequestConfig
    ): Promise<ApiResponse<void>> {
        const url = this.buildURL(endpoint, config?.params);

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: this.mergeHeaders(config?.headers),
            });

            if (!response.ok) {
                return {
                    success: false,
                    error: 'Download failed',
                    statusCode: response.status,
                };
            }

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);

            return {
                success: true,
                message: 'Download successful',
            };
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Download failed',
            };
        }
    }

    /**
     * Batch requests (parallel)
     */
    async batch<T = unknown>(
        requests: Array<{ method: string; endpoint: string; data?: unknown; config?: ApiRequestConfig }>
    ): Promise<Array<ApiResponse<T>>> {
        const promises = requests.map(req =>
            this.request<T>(req.method, req.endpoint, req.data, req.config)
        );
        return Promise.all(promises);
    }

    /**
     * Health check
     */
    async healthCheck(): Promise<ApiResponse<{ status: string }>> {
        return this.get('/health');
    }
}

// ==================== Create Global Instance ====================

export const apiManager = new ApiManager();

// ==================== Export Helper for Custom Instances ====================

export const createApiManager = (customBaseURL?: string): ApiManager => {
    const manager = new ApiManager();
    if (customBaseURL) {
        manager.setBaseURL(customBaseURL);
    }
    return manager;
};

// ==================== Export Types ====================

export type { ApiManager };
